export const IDENTITY_PROVIDERS = {
  GOOGLE_WORKSPACE: 'googleWorkspace',
  FIREBASE_EMAIL_PASSWORD: 'firebaseEmailPassword',
  MIXED: 'mixed'
};

export const ROLE_PROVIDERS = {
  WORKSPACE_GROUPS: 'workspaceGroups',
  APP_MANAGED: 'appManaged'
};

export const DIRECTORY_PROVIDERS = {
  WORKSPACE: 'workspace',
  APP_USERS: 'appUsers',
  PERSONNEL: 'personnel'
};

export const FILE_STORAGE_PROVIDERS = {
  GOOGLE_DRIVE: 'googleDrive',
  FIREBASE_STORAGE: 'firebaseStorage'
};

const VALID_IDENTITY_PROVIDERS = Object.values(IDENTITY_PROVIDERS);
const VALID_ROLE_PROVIDERS = Object.values(ROLE_PROVIDERS);
const VALID_DIRECTORY_PROVIDERS = Object.values(DIRECTORY_PROVIDERS);
const VALID_FILE_STORAGE_PROVIDERS = Object.values(FILE_STORAGE_PROVIDERS);

const getEnvValue = (env, key, fallback = '') => {
  const value = env?.[key];
  return value === undefined || value === null || `${value}`.trim() === ''
    ? fallback
    : `${value}`.trim();
};

const getProviderValue = (env, key, validValues, fallback) => {
  const value = getEnvValue(env, key, fallback);
  return validValues.includes(value) ? value : fallback;
};

const normalizeDomain = (domain) => getEnvValue({ domain }, 'domain')
  .replace(/^@+/, '')
  .toLowerCase();

export const buildDeploymentConfig = (env = process.env) => {
  const identityProvider = getProviderValue(
    env,
    'REACT_APP_IDENTITY_PROVIDER',
    VALID_IDENTITY_PROVIDERS,
    IDENTITY_PROVIDERS.GOOGLE_WORKSPACE
  );

  const roleProvider = getProviderValue(
    env,
    'REACT_APP_ROLE_PROVIDER',
    VALID_ROLE_PROVIDERS,
    identityProvider === IDENTITY_PROVIDERS.FIREBASE_EMAIL_PASSWORD
      ? ROLE_PROVIDERS.APP_MANAGED
      : ROLE_PROVIDERS.WORKSPACE_GROUPS
  );

  const directoryProvider = getProviderValue(
    env,
    'REACT_APP_DIRECTORY_PROVIDER',
    VALID_DIRECTORY_PROVIDERS,
    identityProvider === IDENTITY_PROVIDERS.FIREBASE_EMAIL_PASSWORD
      ? DIRECTORY_PROVIDERS.APP_USERS
      : DIRECTORY_PROVIDERS.WORKSPACE
  );

  const fileStorageProvider = getProviderValue(
    env,
    'REACT_APP_FILE_STORAGE_PROVIDER',
    VALID_FILE_STORAGE_PROVIDERS,
    identityProvider === IDENTITY_PROVIDERS.FIREBASE_EMAIL_PASSWORD
      ? FILE_STORAGE_PROVIDERS.FIREBASE_STORAGE
      : FILE_STORAGE_PROVIDERS.GOOGLE_DRIVE
  );

  const workspaceDomain = normalizeDomain(
    getEnvValue(env, 'REACT_APP_WORKSPACE_DOMAIN', 'craigrescue.com')
  );

  const organizationName = getEnvValue(env, 'REACT_APP_ORGANIZATION_NAME', 'Operations Center');

  return {
    providers: {
      identity: identityProvider,
      role: roleProvider,
      directory: directoryProvider,
      fileStorage: fileStorageProvider
    },
    workspace: {
      domain: workspaceDomain,
      groups: {
        admins: getEnvValue(env, 'REACT_APP_GOOGLE_GROUP_ADMINS', `officers@${workspaceDomain}`),
        inspectors: getEnvValue(env, 'REACT_APP_GOOGLE_GROUP_INSPECTORS', `all@${workspaceDomain}`),
        supervisors: getEnvValue(env, 'REACT_APP_GOOGLE_GROUP_SUPERVISORS', `officers@${workspaceDomain}`)
      }
    },
    appUsers: {
      collection: getEnvValue(env, 'REACT_APP_APP_USERS_COLLECTION', 'appUsers'),
      firstAdminEmail: getEnvValue(env, 'REACT_APP_FIRST_ADMIN_EMAIL', '')
    },
    app: {
      baseUrl: getEnvValue(env, 'REACT_APP_APP_BASE_URL', '')
    },
    authActions: {
      passwordResetUrl: getEnvValue(env, 'REACT_APP_PASSWORD_RESET_URL', getEnvValue(env, 'REACT_APP_APP_BASE_URL', '')),
      dynamicLinkDomain: getEnvValue(env, 'REACT_APP_AUTH_ACTION_LINK_DOMAIN', '')
    },
    labels: {
      appName: getEnvValue(env, 'REACT_APP_APP_NAME', 'Operations Center'),
      appShortName: getEnvValue(env, 'REACT_APP_APP_SHORT_NAME', 'Operations Center'),
      companyName: getEnvValue(env, 'REACT_APP_COMPANY_NAME', 'FireRescue Logix'),
      organizationName,
      organizationShortName: getEnvValue(env, 'REACT_APP_ORGANIZATION_SHORT_NAME', organizationName),
      logoPath: getEnvValue(env, 'REACT_APP_LOGO_PATH', '/stationready-logo.webp'),
      loginLogoPath: getEnvValue(env, 'REACT_APP_LOGIN_LOGO_PATH', '/frlogix-logo-large.webp'),
      tagline: getEnvValue(env, 'REACT_APP_APP_TAGLINE', 'Fleet and operations management'),
      supportEmail: getEnvValue(env, 'REACT_APP_SUPPORT_EMAIL', `officers@${workspaceDomain}`)
    }
  };
};

export const DEPLOYMENT_CONFIG = buildDeploymentConfig();

export const isGoogleWorkspaceEnabled = (
  config = DEPLOYMENT_CONFIG
) => config.providers.identity === IDENTITY_PROVIDERS.GOOGLE_WORKSPACE
  || config.providers.identity === IDENTITY_PROVIDERS.MIXED
  || config.providers.role === ROLE_PROVIDERS.WORKSPACE_GROUPS
  || config.providers.directory === DIRECTORY_PROVIDERS.WORKSPACE;

export const isFirebaseEmailPasswordEnabled = (
  config = DEPLOYMENT_CONFIG
) => config.providers.identity === IDENTITY_PROVIDERS.FIREBASE_EMAIL_PASSWORD
  || config.providers.identity === IDENTITY_PROVIDERS.MIXED;

export const usesFirebaseStorage = (
  config = DEPLOYMENT_CONFIG
) => config.providers.fileStorage === FILE_STORAGE_PROVIDERS.FIREBASE_STORAGE;

export const usesGoogleDriveStorage = (
  config = DEPLOYMENT_CONFIG
) => config.providers.fileStorage === FILE_STORAGE_PROVIDERS.GOOGLE_DRIVE;
